/**
 * Modal de detalle de una compra de stock — Sprint 17 S17-Z10.
 *
 * Muestra todas las unidades de una compra agrupada (mismo tipo, costo,
 * valor de venta y fecha de ingreso), con el estado individual y el
 * destino si fueron asignadas/consumidas. Se abre al hacer click sobre
 * una fila de la vista Stock agrupada.
 *
 * Para llaveros se muestra `identificador` + `serial_rfid` (cada unidad
 * es única). Para CR/pila/otro, solo el id corto del item.
 *
 * Destino: si `recurso_id != null` y/o `consumido_at != null`, indica
 * adónde fue. Por ahora muestra el UUID truncado del recurso destino;
 * en una iteración futura podría enriquecerse con casa + identificador
 * (requiere join backend).
 */
import type {
  EstadoStock,
  StockRecursoRead,
  TipoStock,
} from '../../lib/api/types';
import { formatFecha } from '../../lib/format';
import { Monto, Num } from './Monto';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

export interface ModalDetalleCompraProps {
  items: StockRecursoRead[];
  tipo: TipoStock;
  costoCompra: number;
  valorVenta: number;
  ingresadoAt: string;
  onClose: () => void;
}

const ETIQUETA_TIPO: Record<TipoStock, string> = {
  llavero: 'Llaveros',
  control_remoto: 'Controles remotos',
  pila: 'Pilas',
  otro: 'Otros',
};

const ETIQUETA_ESTADO: Record<EstadoStock, string> = {
  en_stock: 'En stock',
  asignado: 'Asignado',
  consumido: 'Consumido',
  dado_de_baja: 'Dado de baja',
};

const COLOR_ESTADO: Record<EstadoStock, string> = {
  en_stock: 'text-state-success',
  asignado: 'text-state-info',
  consumido: 'text-text-muted',
  dado_de_baja: 'text-state-danger',
};

function formatDestino(item: StockRecursoRead): string {
  if (item.estado === 'en_stock') return '—';
  if (item.estado === 'dado_de_baja') return 'Dado de baja';
  // Destino legible: la casa a la que se asignó (+ el código del recurso como
  // referencia), en vez del UUID.
  const casa = item.recurso_lote_identificador
    ? `Casa ${item.recurso_lote_identificador}`
    : null;
  const ref = item.recurso_identificador ? ` (${item.recurso_identificador})` : '';
  if (item.consumido_at) {
    if (casa) return `Consumido — ${casa}${ref}`;
    if (ref) return `Consumido${ref}`;
    return `Consumido el ${formatFecha(item.consumido_at)}`;
  }
  if (casa) return `Asignado a ${casa}${ref}`;
  if (item.recurso_identificador) return `Asignado (${item.recurso_identificador})`;
  return '—';
}

export function ModalDetalleCompra({
  items,
  tipo,
  costoCompra,
  valorVenta,
  ingresadoAt,
  onClose,
}: ModalDetalleCompraProps) {
  const disponibles = items.filter((i) => i.estado === 'en_stock').length;
  const asignados = items.filter((i) => i.estado === 'asignado').length;
  const consumidos = items.filter((i) => i.estado === 'consumido').length;
  const dadosBaja = items.filter((i) => i.estado === 'dado_de_baja').length;
  const total = items.length;

  return (
    <Modal
      title={`Detalle de compra · ${ETIQUETA_TIPO[tipo]}`}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-text-muted uppercase tracking-wide">
              Fecha de ingreso
            </div>
            <div className="text-text-primary mt-0.5">
              {formatFecha(ingresadoAt)}
            </div>
          </div>
          <div>
            <div className="text-text-muted uppercase tracking-wide">
              Costo compra
            </div>
            <div className="text-text-primary mt-0.5">
              <Monto value={costoCompra} />
            </div>
          </div>
          <div>
            <div className="text-text-muted uppercase tracking-wide">
              Valor venta
            </div>
            <div className="text-text-primary mt-0.5">
              <Monto value={valorVenta} />
            </div>
          </div>
          <div>
            <div className="text-text-muted uppercase tracking-wide">
              Unidades
            </div>
            <div className="text-text-primary mt-0.5">
              <Num>{total}</Num>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 text-xs text-text-muted">
          <span>
            <span className="text-state-success font-medium">{disponibles}</span>{' '}
            disponibles
          </span>
          <span>
            <span className="text-state-info font-medium">{asignados}</span>{' '}
            asignadas
          </span>
          <span>
            <span className="text-text-secondary font-medium">{consumidos}</span>{' '}
            consumidas
          </span>
          {dadosBaja > 0 && (
            <span>
              <span className="text-state-danger font-medium">{dadosBaja}</span>{' '}
              dadas de baja
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-text-secondary border-b border-white/[0.08]">
              <tr>
                {tipo === 'llavero' && (
                  <>
                    <th className="text-left px-2 py-1.5 font-medium">
                      Identificador
                    </th>
                    <th className="text-left px-2 py-1.5 font-medium">
                      N° serie
                    </th>
                  </>
                )}
                {tipo !== 'llavero' && (
                  <th className="text-left px-2 py-1.5 font-medium">Item</th>
                )}
                <th className="text-left px-2 py-1.5 font-medium">Estado</th>
                <th className="text-left px-2 py-1.5 font-medium">Destino</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  className="border-b border-white/[0.04]"
                  data-testid="fila-detalle-compra"
                >
                  {tipo === 'llavero' && (
                    <>
                      <td className="px-2 py-1.5 text-text-primary">
                        {it.identificador ? (
                          <Num>{it.identificador}</Num>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-text-muted">
                        {it.serial_rfid ? <Num>{it.serial_rfid}</Num> : '—'}
                      </td>
                    </>
                  )}
                  {tipo !== 'llavero' && (
                    <td className="px-2 py-1.5 text-text-muted">
                      <Num>#{it.id.slice(0, 8)}</Num>
                    </td>
                  )}
                  <td
                    className={`px-2 py-1.5 font-medium ${COLOR_ESTADO[it.estado]}`}
                  >
                    {ETIQUETA_ESTADO[it.estado]}
                  </td>
                  <td className="px-2 py-1.5 text-text-secondary text-xs">
                    {formatDestino(it)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
